import React, { useEffect, useState } from 'react';
import {
  Form,
  Row,
  Col,
  Typography,
  TimePicker,
  TabsProps,
  Tabs,
  Input,
} from 'antd';
import Modal from '../../common/Modal';
import styled from 'styled-components';
import { FONT_WEIGHTS, COLORS, TEXT_SIZES } from '../../../utils/constants';
import { Dayjs } from 'dayjs';
import { PlusCircleFilled, CloseOutlined } from '@ant-design/icons';
import Button from '../../common/Button';
import { createSlotsRequest } from '../../../api/requests/slot';
import {
  getDeviceTimeZone,
  localDateToUTCString,
} from '../../../utils/dateUtils';
import { CreateSlotType } from '../../../types/types';
import { useSnackbar } from '../../../contexts/SnackbarContext/SnackbarProvider';

interface CreateSlotModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: () => void;
  courtName: string;
  courtId?: number;
  selectedDate?: Dayjs;
}

const TAB_KEYS = {
  Manaul: '1',
  Hourly: '2',
} as const;

const CreateSlotModal: React.FC<CreateSlotModalProps> = ({
  visible,
  onClose,
  onSubmit,
  courtName,
  courtId,
  selectedDate,
}) => {
  const [form] = Form.useForm();
  const [hourlyForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState<string>(TAB_KEYS.Manaul);
  const snackbar = useSnackbar();
  const [repeatDays, setRepeatDays] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    if (visible) {
      form.setFieldsValue({
        slots: [{ startTime: null, endTime: null }],
      });
      hourlyForm.setFieldsValue({
        startTime: null,
        endTime: null,
      });
    }
  }, [visible, form, hourlyForm]);

  const generateHourlySlots = (startTime: Dayjs, endTime: Dayjs) => {
    const slots = [];

    if (!startTime.isBefore(endTime)) {
      slots.push({
        startTime,
        endTime,
      });
      return slots;
    }

    let current = startTime.clone();

    while (current.isBefore(endTime)) {
      const slotEnd = current.add(1, 'hour');
      if (slotEnd.isAfter(endTime)) break;

      slots.push({
        startTime: current,
        endTime: slotEnd,
      });
      current = slotEnd;
    }

    return slots;
  };

  const formatSlots = (slots: any[]) => {
    if (!selectedDate) return [];
    return slots.map((slot: any) => {
      const startTime = slot.startTime.hour() * 60 + slot.startTime.minute();
      const endTime = slot.endTime.hour() * 60 + slot.endTime.minute();

      const startDateTime = selectedDate
        .startOf('day')
        .add(startTime, 'minutes');
      const endDateTime = selectedDate.startOf('day').add(endTime, 'minutes');

      return {
        startTime: localDateToUTCString(startDateTime?.toDate()),
        endTime: localDateToUTCString(endDateTime?.toDate()),
      };
    });
  };

  const handleSubmit = async () => {
    try {
      if (!courtId || !selectedDate) return;

      setIsLoading(true);

      let formattedSlots;

      if (activeTab === '1') {
        const values = await form.validateFields();
        formattedSlots = formatSlots(values.slots);
      } else {
        const values = await hourlyForm.validateFields();
        const hourlySlots = generateHourlySlots(
          values.startTime,
          values.endTime,
        );
        formattedSlots = formatSlots(hourlySlots);
      }

      try {
        const data: CreateSlotType = {
          courtId: courtId,
          slotTimings: formattedSlots,
          repeatForDays: repeatDays,
          timeZoneId: getDeviceTimeZone(),
        };

        const response = await createSlotsRequest(data);
        if (response?.isSuccess) {
          form.resetFields();
          hourlyForm.resetFields();
          onSubmit();
          setIsLoading(false);
          snackbar.success('Slots created successfully');
        } else {
          setIsLoading(false);
          snackbar.error(response?.error);
        }
      } catch (error: any) {
        setIsLoading(false);
        snackbar.error(error?.Error || 'Error creating slots');
      }
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  };

  const ManualTabContent = () => {
    return (
      <>
        <CourtText>{courtName}</CourtText>
        <FormWrapper>
          <Row gutter={16} justify={'start'}>
            <Col span={12}>
              <MandatorySymbol>* </MandatorySymbol>Start Time
            </Col>
            <Col span={12}>
              <MandatorySymbol>* </MandatorySymbol>End Time
            </Col>
          </Row>
          <Form
            form={form}
            layout="vertical"
            onValuesChange={(_, allValues) => {
              const slots = allValues.slots || [];
              const allFilled = slots.every(
                (slot: any) => slot?.startTime && slot?.endTime,
              );
              setCanSubmit(allFilled);
            }}
          >
            <Form.List name="slots">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, idx) => (
                    <SlotPairWrapper key={field.key}>
                      <StyledFormItem
                        name={[field.name, 'startTime']}
                        rules={[
                          {
                            required: true,
                            message: 'Please select start time',
                          },
                        ]}
                      >
                        <TimePicker
                          format="hh:mm A"
                          style={{ width: '100%' }}
                          use12Hours
                          size="large"
                        />
                      </StyledFormItem>
                      <StyledFormItem
                        name={[field.name, 'endTime']}
                        rules={[
                          {
                            required: true,
                            message: 'Please select end time',
                          },
                        ]}
                      >
                        <TimePicker
                          format="hh:mm A"
                          style={{ width: '100%' }}
                          use12Hours
                          size="large"
                        />
                      </StyledFormItem>
                      {fields.length > 1 && (
                        <StyledCloseOutlined
                          onClick={() => remove(field.name)}
                        />
                      )}
                    </SlotPairWrapper>
                  ))}
                  <AddSlotButton>
                    <Button
                      onClick={() => add({ startTime: null, endTime: null })}
                      icon={<StyledPlusIcon />}
                      size="large"
                      style={{ fontSize: TEXT_SIZES.MD }}
                      isBlock
                    >
                      Add New Slot
                    </Button>
                  </AddSlotButton>
                </>
              )}
            </Form.List>
          </Form>
        </FormWrapper>
      </>
    );
  };

  const HourlyTabContent = () => {
    return (
      <>
        <CourtText>{courtName}</CourtText>
        <FormWrapper>
          <Row gutter={16} justify={'start'}>
            <Col span={12}>
              <MandatorySymbol>* </MandatorySymbol>Start Time
            </Col>
            <Col span={12}>
              <MandatorySymbol>* </MandatorySymbol>End Time
            </Col>
          </Row>
          <Form
            form={hourlyForm}
            layout="vertical"
            onValuesChange={(_, values) => {
              setCanSubmit(!!values.startTime && !!values.endTime);
            }}
          >
            <SlotPairWrapper>
              <StyledFormItem
                name="startTime"
                rules={[
                  {
                    required: true,
                    message: 'Please select start time',
                  },
                ]}
              >
                <TimePicker
                  format="hh:mm A"
                  style={{ width: '100%' }}
                  use12Hours
                  size="large"
                />
              </StyledFormItem>
              <StyledFormItem
                name="endTime"
                rules={[
                  {
                    required: true,
                    message: 'Please select end time',
                  },
                ]}
              >
                <TimePicker
                  format="hh:mm A"
                  style={{ width: '100%' }}
                  use12Hours
                  size="large"
                />
              </StyledFormItem>
            </SlotPairWrapper>
          </Form>
          <HourlyNote>
            Slots will be automatically generated on hourly basis between the
            selected time range
          </HourlyNote>
        </FormWrapper>
      </>
    );
  };

  const tabItems: TabsProps['items'] = [
    {
      key: TAB_KEYS.Manaul,
      label: 'Manual Slots',
      children: <ManualTabContent />,
    },
    {
      key: TAB_KEYS.Hourly,
      label: 'Hourly Slots',
      children: <HourlyTabContent />,
    },
  ];

  const handleTabChange = (key: string) => {
    setActiveTab(key);
  };

  useEffect(() => {
    if (activeTab === TAB_KEYS.Manaul) {
      const slots = form.getFieldValue('slots') || [];
      const allFilled = slots.every(
        (slot: any) => slot?.startTime && slot?.endTime,
      );
      setCanSubmit(allFilled);
    } else {
      const values = hourlyForm.getFieldsValue();
      setCanSubmit(!!values.startTime && !!values.endTime);
    }
  }, [activeTab, form, hourlyForm]);

  return (
    <CreateModal
      title="Create Game Slot"
      open={visible}
      onCancel={onClose}
      minHeight={'auto'}
      footer={
        <>
          <Button key="cancel" onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
            isDisabled={isLoading || !canSubmit}
          >
            {activeTab === TAB_KEYS.Manaul ? 'Create Slots' : 'Generate Slots'}
          </Button>
        </>
      }
    >
      <HeaderWrapper>
        <DateText>Date: {selectedDate?.format('ddd, MMM D')}</DateText>
        <SubText>Creating new slot for customer bookings</SubText>
      </HeaderWrapper>
      <BodyWrapper>
        <Tabs
          defaultActiveKey="1"
          items={tabItems}
          onChange={handleTabChange}
        />
        <RepeatWrapper>
          <Typography.Text>Repeat this slot pattern for</Typography.Text>
          <StyledNumberInput
            type="number"
            min={1}
            max={365}
            value={repeatDays}
            onChange={(e) => setRepeatDays(parseInt(e.target.value))}
          />
          <Typography.Text>days</Typography.Text>
        </RepeatWrapper>
      </BodyWrapper>
    </CreateModal>
  );
};

const CreateModal = styled(Modal)`
  .ant-modal-body {
    padding: 0px !important;
  }
`;

const HeaderWrapper = styled(Col)`
  display: flex;
  flex-direction: column;
  background-color: ${COLORS.grey.light};
  height: 64px;
  padding: 12px 24px;
`;

const BodyWrapper = styled(Col)`
  padding: 12px 24px 24px 24px;
`;

const DateText = styled(Typography.Text)`
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const SubText = styled(Typography.Text)`
  color: ${COLORS.grey.secondary};
`;

const CourtText = styled(Typography.Text)`
  font-size: ${TEXT_SIZES.MD}px;
  font-weight: ${FONT_WEIGHTS.MEDIUM};
`;

const FormWrapper = styled.div`
  margin-top: 12px;
`;

const AddSlotButton = styled.div`
  margin-top: 16px;
`;

const SlotPairWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 8px 0px;
`;

const StyledPlusIcon = styled(PlusCircleFilled)`
  font-size: ${TEXT_SIZES.BASE};
`;

const MandatorySymbol = styled.span`
  color: red;
`;

const StyledCloseOutlined = styled(CloseOutlined)`
  color: ${COLORS.red.primary};
  font-size: ${TEXT_SIZES.BASE};
  cursor: pointer;
`;

const StyledFormItem = styled(Form.Item)`
  width: 100%;
  flex: 1;
  margin-bottom: 0;
`;

const HourlyNote = styled(Typography.Text)`
  color: ${COLORS.grey.secondary};
  font-size: ${TEXT_SIZES.SM};
  font-style: italic;
  margin-top: 12px;
  display: block;
`;

const RepeatWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 16px;
  background-color: ${COLORS.grey.light};
  border-radius: 8px;
`;

const StyledNumberInput = styled(Input)`
  width: 80px;
  margin: 0 8px;
`;
export default CreateSlotModal;
